// src/components/DePINVideoRecorder.js - ЗАПИСЬ DEPИН ВИДЕО

import DePINNetworkAnimation from './DePINNetworkAnimation';

class DePINVideoRecorder {
  constructor(map) {
    this.map = map;
    this.depinAnimation = new DePINNetworkAnimation(map);
    this.isRecording = false;
    
    // 📹 ВИДЕО ЗАПИСЬ
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.recordingQuality = 'web';
    
    console.log('📹 DePIN Video Recorder initialized');
  }

  // ⚙️ НАСТРОЙКИ КАЧЕСТВА
  getVideoSettings(quality = 'web') {
    const settings = {
      mobile: {
        fps: 30,
        bitrate: 1500000, // 1.5 Mbps для DePIN анимации
        duration: 35000   // 35 секунд
      },
      web: {
        fps: 30,
        bitrate: 4000000, // 4 Mbps для качественного DePIN
        duration: 35000   // 35 секунд
      },
      hd: {
        fps: 30,
        bitrate: 8000000, // 8 Mbps для HD DePIN
        duration: 35000   // 35 секунд
      }
    };
    
    return settings[quality] || settings.web;
  }

  // 🔍 ПРОВЕРКА ГОТОВНОСТИ
  checkReadiness() {
    if (!this.map) {
      console.error('❌ Map not available');
      return false;
    }
    
    if (!this.map.getCanvas) {
      console.error('❌ Map canvas not available');
      return false;
    }
    
    if (!this.map.isStyleLoaded()) {
      console.error('❌ Map style not loaded');
      return false;
    }
    
    return true;
  }

  // 📹 ЗАПИСЬ ВИДЕО
  async startVideoRecording(quality = 'web') {
    if (!this.checkReadiness()) {
      return false;
    }
    
    try {
      console.log(`📹 Starting DePIN video recording: ${quality}`);
      
      const settings = this.getVideoSettings(quality);
      const canvas = this.map.getCanvas();
      const stream = canvas.captureStream(settings.fps);
      
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8',
        videoBitsPerSecond: settings.bitrate
      });
      
      this.recordedChunks = [];
      this.recordingQuality = quality;
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };
      
      this.mediaRecorder.onstop = () => {
        this.saveVideo();
      };
      
      this.mediaRecorder.start(1000);
      this.isRecording = true;
      
      console.log(`✅ DePIN recording started - ${settings.duration/1000}s @ ${settings.bitrate/1000000}Mbps`);
      return true;
      
    } catch (error) {
      console.error('❌ DePIN recording error:', error);
      return false;
    }
  }

  // 💾 СОХРАНЕНИЕ ВИДЕО
  saveVideo() {
    if (this.recordedChunks.length === 0) {
      console.warn('⚠️ No DePIN video data to save');
      return;
    }

    const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
    const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    
    a.href = url;
    a.download = `DePIN-network-growth-${this.recordingQuality}-${timestamp}.webm`;
    a.style.display = 'none';
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log(`💾 DePIN video saved: ${sizeMB} MB`);
    this.showNotification(`🌐 DePIN Network: ${sizeMB} MB`);
  }

  // 💾 УВЕДОМЛЕНИЕ
  showNotification(message) {
    const div = document.createElement('div');
    div.style.cssText = `
      position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
      z-index: 20000; background: #3A33FF; color: white;
      padding: 15px 25px; border-radius: 10px; font-weight: bold;
      box-shadow: 0 5px 15px rgba(58, 51, 255, 0.3);
    `;
    div.innerHTML = `💾 ${message}`;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 4000);
  }

  // 🙈 СКРЫТЬ UI ДЛЯ ЗАПИСИ
  hideUIElements() {
    const selectors = [
      '#tour-controls', '#flyover-controls', '#drone-controls', '#depin-controls',
      '.maplibregl-ctrl-top-left', '.maplibregl-ctrl-top-right',
      '.maplibregl-ctrl-bottom-left', '.maplibregl-ctrl-bottom-right',
      '.h3-controls', '.heatmap-controls'
    ];

    this.hiddenElements = [];
    
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        if (el && el.style.display !== 'none') {
          this.hiddenElements.push({
            element: el,
            originalDisplay: el.style.display
          });
          el.style.display = 'none';
        }
      });
    });

    console.log(`🙈 Hidden ${this.hiddenElements.length} elements for DePIN recording`);
  }

  // 👁️ ПОКАЗАТЬ UI
  showUIElements() {
    if (this.hiddenElements) {
      this.hiddenElements.forEach(({element, originalDisplay}) => {
        element.style.display = originalDisplay;
      });
      console.log(`👁️ Restored ${this.hiddenElements.length} elements`);
      this.hiddenElements = [];
    }
  }

  // ⏹️ ОСТАНОВКА
  stop() {
    console.log('⏹️ Stopping DePIN recording and animation');
    
    // Останавливаем анимацию
    this.depinAnimation.stopAnimation();
    
    // Останавливаем запись
    if (this.isRecording && this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.isRecording = false;
      setTimeout(() => this.showUIElements(), 1000);
    }
  }

  // 📱 МОБИЛЬНАЯ ЗАПИСЬ
  async recordMobile() {
    if (this.isRecording || this.depinAnimation.isAnimating) {
      console.log('🌐 DePIN already running');
      return;
    }
    
    console.log('📱 Starting mobile DePIN recording...');
    
    this.hideUIElements();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const recordingStarted = await this.startVideoRecording('mobile');
    if (!recordingStarted) {
      this.showUIElements();
      return;
    }
    
    // Запускаем анимацию
    await this.depinAnimation.startAnimation();
    
    // Останавливаем запись через 35 секунд
    setTimeout(() => {
      if (this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
      }
      setTimeout(() => this.showUIElements(), 1000);
    }, 37000); // 35s + 2s буфер
  }

  // 🌐 ВЕБ ЗАПИСЬ
  async recordWeb() {
    if (this.isRecording || this.depinAnimation.isAnimating) {
      console.log('🌐 DePIN already running');
      return;
    }
    
    console.log('🌐 Starting web DePIN recording...');
    
    this.hideUIElements();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const recordingStarted = await this.startVideoRecording('web');
    if (!recordingStarted) {
      this.showUIElements();
      return;
    }
    
    // Запускаем анимацию
    await this.depinAnimation.startAnimation();
    
    // Останавливаем запись через 35 секунд
    setTimeout(() => {
      if (this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
      }
      setTimeout(() => this.showUIElements(), 1000);
    }, 37000); // 35s + 2s буфер
  }

  // 🎬 ОБЫЧНАЯ АНИМАЦИЯ (БЕЗ ЗАПИСИ)
  async startAnimation() {
    if (this.depinAnimation.isAnimating) {
      console.log('🌐 DePIN animation already running');
      return;
    }
    
    console.log('🎬 Starting DePIN animation preview...');
    await this.depinAnimation.startAnimation();
  }

  // 📊 СТАТИСТИКА
  getStats() {
    return {
      isRecording: this.isRecording,
      recordingQuality: this.recordingQuality,
      animationStats: this.depinAnimation.getStats()
    };
  }
}

// 🎮 КОНТРОЛЛЕР DEPIN
class DePINController {
  constructor(map) {
    this.recorder = new DePINVideoRecorder(map);
    this.createControls();
    this.setupKeyboardControls();
    console.log('🌐 DePIN Controller ready');
  }

  setupKeyboardControls() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'F1') {
        event.preventDefault();
        this.recorder.startAnimation();
      }
      
      if (event.key === 'F2') {
        event.preventDefault();
        this.recorder.recordWeb();
      }
      
      if (event.key === 'F3') {
        event.preventDefault();
        this.recorder.recordMobile();
      }
      
      if (event.key === 'Escape') {
        this.recorder.stop();
      }
    });
    
    console.log('⌨️ DePIN Hotkeys:');
    console.log('   F1 - DePIN Animation');
    console.log('   F2 - Web Recording');
    console.log('   F3 - Mobile Recording');
    console.log('   ESC - Stop');
  }

  createControls() {
    const existing = document.getElementById('depin-controls');
    if (existing) existing.remove();

    const div = document.createElement('div');
    div.id = 'depin-controls';
    div.style.cssText = `
      position: absolute; bottom: 140px; left: 50%; transform: translateX(-50%);
      z-index: 1000; background: rgba(58,51,255,0.9); padding: 15px;
      border-radius: 10px; display: flex; gap: 8px;
    `;
    
    div.innerHTML = `
      <button id="web-depin" style="background: #007AFF; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        🌐 WEB (F2)
      </button>
      <button id="mobile-depin" style="background: #34C759; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        📱 MOBILE (F3)
      </button>
      <button id="normal-depin" style="background: #8E8E93; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        🌐 DePIN (F1)
      </button>
      <button id="stop-depin" style="background: #FF453A; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        ⏹️ STOP
      </button>
    `;
    
    document.body.appendChild(div);
    
    document.getElementById('web-depin').onclick = () => this.recorder.recordWeb();
    document.getElementById('mobile-depin').onclick = () => this.recorder.recordMobile();
    document.getElementById('normal-depin').onclick = () => this.recorder.startAnimation();
    document.getElementById('stop-depin').onclick = () => this.recorder.stop();

    console.log('🎮 DePIN controls created');
  }
}

export { DePINVideoRecorder, DePINController };